import React, { useEffect } from 'react';
import useToggle from 'hooks/use-toggle';
import classNames from 'classnames';
import Button from 'atomic/button';
import Icon from 'atomic/icon';
import scrollToElement from 'helpers/scroll-to-element';

import './expander.scss';

export interface ExpanderProps {
    content: string;
    minLength?: number;
    expanded?: boolean;
    shadowColorGradient?: 'white' | 'gray';
    anchorIdName?: string;
    onClose?: () => void;
    onOpen?: () => void;
    isReadMore?: boolean;
}

const Expander: React.FC<ExpanderProps> = (
    {
        content,
        minLength = 800,
        expanded = false,
        shadowColorGradient = 'white',
        anchorIdName,
        onClose,
        onOpen,
        isReadMore = false
    }
) => {
    const [show, setShow] = useToggle(expanded);

    useEffect(() => {
        setShow.setToggleState(expanded);
    }, [expanded, setShow]);

    if (content?.length < minLength) {
        return (
            <div
                className="expander"
                dangerouslySetInnerHTML={{ __html: content }}
            />
        );
    }

    const onCLick = (): void => {
        if (anchorIdName && show) {
            scrollToElement(anchorIdName);
        }

        if (onClose && show) {
            onClose();
        }

        if (onOpen && !show) {
            onOpen();
        }

        setShow.toggle();
    };

    return (
        <div className="expander">
            <div
                className={classNames({
                    'expander__text': true,
                    'expander__text--read-more': isReadMore,
                    'expander__text--show-more': show
                })}
                dangerouslySetInnerHTML={{ __html: content }}
            />

            {!show && content?.length > minLength && (
                <div
                    className={classNames({
                        'expander__shadow': true,
                        'expander__shadow--read-more': isReadMore,
                        [`expander__shadow--${shadowColorGradient}`]: true

                    })}
                />
            )}
            {!isReadMore && content?.length > minLength && (
                <div className="expander__button">
                    <Button
                        size="md"
                        color="dark"
                        onClick={onCLick}
                        data-cy="show-text"
                    >
                        <Icon name={show ? 'minus' : 'plus'} color="dark" size={14} />
                        {show ? 'Zwiń opis' : 'Rozwiń opis'}
                    </Button>
                </div>
            )}
            {isReadMore && (
                <div
                    className={classNames({
                        'expander__button--read-more': isReadMore,
                        'expander__button--show-more': isReadMore && !show,
                        [`expander__button--${shadowColorGradient}`]: isReadMore && !show
                    })}
                >
                    <Button
                        tag="span"
                        size="xs"
                        color="transparent-secondary"
                        uppercase={true}
                        onClick={onCLick}
                    >
                        {show ? 'Zwiń' : 'Czytaj więcej...'}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default Expander;
